import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { isNull } from 'lodash';
import { AnyAction } from 'redux';

import { actionTypes } from 'actions/session';

import {
  ExperimentalBundles,
  HiringAssistantProduct,
  ManagePlanPageAttributes,
  PlanType,
  PurchasableProducts,
} from './types';

export interface Plan {
  tier_id: number;
  tier_name: string;
  savings: number | null;
  suggested_plan: boolean;
  billing_frequency: string;
  downsell_tier_name: string;
  monthly_base_amount: number;
  monthly_discounted_amount: number;
}

export interface FeatureType {
  id: number;
  name: string;
}

export interface PriceType {
  amountInCents?: number | null;
  priceSystemId: string;
}

export interface PriceApiType {
  amount_in_cents: number;
  system_id: string;
}

export interface PurchasableProductsAPIType {
  products: PurchasableProducts;
}

export interface ManagePlanPageAttributesApiType {
  attributes: ManagePlanPageAttributes;
}

export interface ProductApiType {
  task_manager?: TaskManagerProductApiType[];
  tip_manager?: TipManagerProductApiType[];
  hiring_assistant?: HiringAssistantProductApiType[];
}

export interface TipManagerProductApiType {
  price: PriceApiType[];
}

export interface TaskManagerProductApiType {
  price: PriceApiType[];
}

export interface HiringAssistantProductApiType extends HiringAssistantProduct {}

export interface ProductsApiType {
  products?: ProductApiType;
}

export interface SubscriptionApiType {
  archived_at?: null | Date;
  price_in_cents?: null | number;
}

export interface SubscriptionsApiType {
  subscriptions?: SubscriptionApiType[];
}

function updateSessionMatcher(action: AnyAction): action is PayloadAction<{
  tierProducts?: {
    billed_annually: boolean;
    monthly: PlanType[];
    annual: PlanType[];
  };
}> {
  return action.type === actionTypes.UPDATE_SESSION;
}

import {
  fetchExperimentalBundles,
  fetchFeatures,
  fetchHiringAssistantProduct,
  fetchHiringAssistantSubscriptions,
  fetchManagePlanPageAttributes,
  fetchMarketingDiscountCoupon,
  fetchProducts,
  fetchProductTaskManager,
  fetchProductTipManager,
  fetchPurchasableProducts,
  fetchTaskManagerSubscriptions,
  fetchTipManagerSubscriptions,
} from './actions';
import { getBillingCycleIsAnnual } from './util';

export const initialState = {
  attributes: {} as ManagePlanPageAttributes,
  purchasableProducts: {} as PurchasableProducts,
  monthlyProducts: [] as PlanType[],
  annualProducts: [] as PlanType[],
  billed_annually: null,
  isSelectedBillingCycleAnnual: false,
  features: [] as FeatureType[],
  taskManager: {
    isLoading: false,
    isSubscribed: false,
    price: { amountInCents: 0, priceSystemId: '' } as PriceType,
  },
  tipManager: {
    isLoading: false,
    isSubscribed: false,
    price: { amountInCents: 0, priceSystemId: '' } as PriceType,
  },
  hiringAssistant: {
    isLoading: false,
    isSubscribed: false,
    price: { amountInCents: 0, priceSystemId: '' } as PriceType,
    productId: 0,
  },
  marketingAttachedDiscountCoupon: undefined,
  experimentalBundles: {
    isLoading: false,
    bundles: {} as ExperimentalBundles,
  },
};

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    toggleBillingFrequency: (state, { payload }) => {
      state.isSelectedBillingCycleAnnual = payload === 'annual';
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchPurchasableProducts.fulfilled, (state, action) => {
        state.purchasableProducts = action.payload.products;
      })
      .addCase(fetchProducts.pending, state => {
        state.monthlyProducts = [];
        state.annualProducts = [];
      })
      .addCase(fetchProducts.fulfilled, (state, action) => {
        state.monthlyProducts = action.payload.monthly;
        state.annualProducts = action.payload.annual;
        state.isSelectedBillingCycleAnnual = getBillingCycleIsAnnual(
          action.payload
        );
      })
      .addCase(fetchFeatures.pending, state => {
        state.features = [];
      })
      .addCase(fetchFeatures.fulfilled, (state, action) => {
        state.features = action.payload.features;
      })
      .addCase(fetchMarketingDiscountCoupon.pending, state => {
        state.marketingAttachedDiscountCoupon = undefined;
      })
      .addCase(fetchMarketingDiscountCoupon.fulfilled, (state, action) => {
        state.marketingAttachedDiscountCoupon = action.payload.coupon;
      })
      .addCase(fetchTaskManagerSubscriptions.pending, state => {
        state.taskManager.isLoading = true;
      })
      .addCase(fetchTaskManagerSubscriptions.fulfilled, (state, action) => {
        const activeSubscription = action.payload.subscriptions?.find(
          (subscription: SubscriptionApiType) =>
            isNull(subscription?.archived_at)
        );
        if (activeSubscription) {
          state.taskManager.price.amountInCents =
            activeSubscription.price_in_cents;
        }
        state.taskManager.isSubscribed = Boolean(activeSubscription);
        state.taskManager.isLoading = false;
      })
      .addCase(fetchProductTaskManager.rejected, state => {
        state.taskManager.isLoading = false;
      })
      .addCase(fetchTipManagerSubscriptions.pending, state => {
        state.tipManager.isLoading = true;
      })
      .addCase(fetchTipManagerSubscriptions.fulfilled, (state, action) => {
        const activeSubscription = action.payload.subscriptions?.find(
          (subscription: SubscriptionApiType) =>
            isNull(subscription?.archived_at)
        );
        if (activeSubscription) {
          state.tipManager.price.amountInCents =
            activeSubscription.price_in_cents;
        }
        state.tipManager.isSubscribed = Boolean(activeSubscription);
        state.tipManager.isLoading = false;
      })
      .addCase(fetchProductTaskManager.fulfilled, (state, action) => {
        // Will be empty if already subscribed
        const taskManager = action.payload.products?.task_manager;
        if (!taskManager || !taskManager.length) return;

        const product = taskManager[0];
        if (!product?.price?.length) return;

        const price = product.price[0];
        state.taskManager.price.amountInCents = price.amount_in_cents;
        state.taskManager.price.priceSystemId = price.system_id;
      })
      .addCase(fetchProductTipManager.fulfilled, (state, action) => {
        // Will be empty if already subscribed
        const tipManager = action.payload.products?.tip_manager;
        if (!tipManager || !tipManager.length) return;

        const product = tipManager[0];
        if (!product?.price?.length) return;

        const price = product.price[0];
        state.tipManager.price.amountInCents = price.amount_in_cents;
        state.tipManager.price.priceSystemId = price.system_id;
      })
      .addCase(fetchProductTipManager.rejected, state => {
        state.taskManager.isLoading = false;
      })
      .addCase(fetchHiringAssistantProduct.pending, state => {
        state.hiringAssistant.isLoading = true;
      })
      .addCase(fetchHiringAssistantProduct.fulfilled, (state, action) => {
        // Will be empty if already subscribed
        const hiringAssistant = action.payload.products?.hiring_assistant;
        if (!hiringAssistant || !hiringAssistant.length) return;

        const product = hiringAssistant[0];
        if (!product?.price?.length) return;

        const price = product.price[0];

        state.hiringAssistant.productId = product.product_id;
        state.hiringAssistant.price.amountInCents = price.amount_in_cents;
        state.hiringAssistant.price.priceSystemId = price.system_id;
        state.hiringAssistant.isLoading = false;
      })
      .addCase(fetchHiringAssistantProduct.rejected, state => {
        state.hiringAssistant.isLoading = false;
      })
      .addCase(fetchManagePlanPageAttributes.pending, state => {
        state.attributes = {};
      })
      .addCase(fetchManagePlanPageAttributes.fulfilled, (state, action) => {
        state.attributes = action.payload.attributes;
      })
      .addCase(fetchHiringAssistantSubscriptions.pending, state => {
        state.hiringAssistant.isLoading = true;
      })
      .addCase(fetchHiringAssistantSubscriptions.fulfilled, (state, action) => {
        const activeSubscription = action.payload.subscriptions?.find(
          (subscription: SubscriptionApiType) =>
            isNull(subscription?.archived_at)
        );
        if (activeSubscription) {
          state.hiringAssistant.price.amountInCents =
            activeSubscription.price_in_cents;
        }
        state.hiringAssistant.isSubscribed = Boolean(activeSubscription);
        state.hiringAssistant.isLoading = false;
      })
      .addCase(fetchHiringAssistantSubscriptions.rejected, state => {
        state.hiringAssistant.isLoading = false;
      })
      .addCase(fetchExperimentalBundles.pending, state => {
        state.experimentalBundles.isLoading = true;
      })
      .addCase(fetchExperimentalBundles.fulfilled, (state, action) => {
        state.experimentalBundles.bundles = action.payload.experimental_bundles;
        state.experimentalBundles.isLoading = false;
      })
      .addCase(fetchExperimentalBundles.rejected, state => {
        state.experimentalBundles.isLoading = false;
      })
      .addMatcher(updateSessionMatcher, (state, action) => {
        const { tierProducts } = action.payload;
        if (!tierProducts) return state;

        state.monthlyProducts = tierProducts.monthly;
        state.annualProducts = tierProducts.annual;
        state.isSelectedBillingCycleAnnual =
          getBillingCycleIsAnnual(tierProducts);
      });
  },
});

export const actions = productsSlice.actions;
export const reducer = productsSlice.reducer;
